// MyBookings.js
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaSearch, FaSync, FaFilter, FaTimes, FaEye,
  FaCalendarAlt, FaTags, FaChevronDown, FaUser,
  FaCreditCard, FaFileInvoice, FaGlobe, FaMoneyBillWave,
  FaCheckCircle, FaHourglassHalf, FaTimesCircle, FaUserCircle
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import Footer from "./Footer";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ─────────────────────────────────────────────────────
   TOAST
───────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  const cfg = {
    success: { icon: <FaCheckCircle />, label: "Success", accent: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", border: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />,       label: "Error",   accent: "#8b1a1a", iconBg: "rgba(139,26,26,0.10)", border: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaSearch />,      label: "Notice",  accent: "#8a6020", iconBg: "rgba(201,151,58,0.10)", border: "rgba(201,151,58,0.30)", symbol: "ॐ" },
  };
  const c = cfg[type] || cfg.info;

  return (
    <div className="mb-toast-wrap">
      <div className="mb-toast" style={{ "--accent": c.accent, "--border": c.border }}>
        <div className="mb-toast-bar" />
        <span className="mb-tc tl" /><span className="mb-tc tr" />
        <span className="mb-tc bl" /><span className="mb-tc br" />
        <div className="mb-toast-medal" style={{ background: c.iconBg, borderColor: c.border }}>
          <div className="mb-toast-spin" />
          <span style={{ color: c.accent, fontSize: 14, position: "relative", zIndex: 1 }}>{c.icon}</span>
        </div>
        <div className="mb-toast-body">
          <div className="mb-toast-row">
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
            <span className="mb-toast-title" style={{ color: c.accent }}>{c.label}</span>
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
          </div>
          <div className="mb-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accent}55,transparent)` }} />
          <p className="mb-toast-msg">{message}</p>
        </div>
        <button className="mb-toast-x" onClick={onClose} style={{ color: c.accent }}>✕</button>
        <div className="mb-toast-foot" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   BOOKING DETAIL MODAL
───────────────────────────────────────────────────── */
function BookingDetailModal({ booking, onClose }) {
  const { t } = useTranslation();

  // ── hooks must be before any early return ──
  useEffect(() => {
    if (!booking) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, booking]);

  if (!booking) return null;

  const isOnline = booking.booked_by === "online";
  const statusCfg = {
    success: { color: "#2a6b2a", bg: "rgba(42,107,42,0.10)", icon: <FaCheckCircle /> },
    pending: { color: "#c9973a", bg: "rgba(201,151,58,0.10)", icon: <FaHourglassHalf /> },
    failed:  { color: "#8b1a1a", bg: "rgba(139,26,26,0.10)", icon: <FaTimesCircle /> },
  };
  const sc = statusCfg[booking.payment_status] || statusCfg.pending;

  let persons = booking.persons;
  if (typeof persons === "string") { try { persons = JSON.parse(persons); } catch { persons = []; } }
  if (!Array.isArray(persons)) persons = [];

  return createPortal(
    <div className="mb-overlay" onClick={onClose}>
      <div className="mb-modal" onClick={e => e.stopPropagation()}>
        {/* Top strip */}
        <div className="mb-modal-strip" />
        <span className="mb-mc tl" /><span className="mb-mc tr" />
        <span className="mb-mc bl" /><span className="mb-mc br" />

        {/* Header */}
        <div className="mb-modal-head">
          <div className="mb-mh-bg" />
          <div className="mb-mh-inner">
            <div className="mb-mh-icon-wrap">
              <div className="mb-mh-icon-ring" />
              <FaFileInvoice className="mb-mh-icon" />
            </div>
            <span className="mb-mh-eyebrow">✦ {t("bookingDetails") || "Booking Details"} ✦</span>
            <h2 className="mb-mh-receipt">{booking.receipt_number}</h2>
            <p className="mb-mh-cat">{booking.category_name}</p>
            <div className="mb-mh-badges">
              <span className={`mb-source-badge ${isOnline ? "online" : "offline"}`}>
                {isOnline ? <FaGlobe /> : <FaMoneyBillWave />}
                {isOnline ? (t("sourceOnline") || "Online") : (t("sourceCounter") || "Counter")}
              </span>
              <span className="mb-pay-mode-badge">{booking.payment_mode?.toUpperCase() || "—"}</span>
            </div>
          </div>
          <button className="mb-modal-close" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="mb-modal-rule"><span className="mb-rule-gem">❖</span></div>

        {/* Scroll body */}
        <div className="mb-modal-body">
          {/* Summary grid */}
          <div className="mb-section-label">{t("summary") || "Summary"}</div>
          <div className="mb-detail-grid">
            <div className="mb-di">
              <div className="mb-di-icon"><FaCalendarAlt /></div>
              <div>
                <div className="mb-di-label">{t("detailBookingDate") || "Booking Date"}</div>
                <div className="mb-di-val">
                  {new Date(booking.booking_date).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
                </div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon"><FaTags /></div>
              <div>
                <div className="mb-di-label">{t("detailCategory") || "Vazhipadu"}</div>
                <div className="mb-di-val">{booking.category_name}</div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon"><FaCreditCard /></div>
              <div>
                <div className="mb-di-label">{t("detailTotalPaid") || "Amount Paid"}</div>
                <div className="mb-di-val" style={{ color: "#2a6b2a", fontWeight: 700 }}>
                  ₹{parseFloat(booking.amount || booking.unit_price || 0).toLocaleString("en-IN")}
                </div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon" style={{ color: sc.color }}>{sc.icon}</div>
              <div>
                <div className="mb-di-label">{t("tableStatus") || "Status"}</div>
                <div className="mb-di-val" style={{ color: sc.color, textTransform: "capitalize" }}>
                  {booking.payment_status}
                </div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon"><FaUser /></div>
              <div>
                <div className="mb-di-label">{t("tablePersons") || "Total Persons"}</div>
                <div className="mb-di-val">{booking.total_persons || 1}</div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon">{isOnline ? <FaGlobe /> : <FaMoneyBillWave />}</div>
              <div>
                <div className="mb-di-label">{t("tableSource") || "Booked Via"}</div>
                <div className="mb-di-val">{isOnline ? "Online" : "Counter"}</div>
              </div>
            </div>
          </div>

          {/* Persons list */}
          {persons.length > 0 && (
            <>
              <div className="mb-section-label" style={{ marginTop: 20 }}>
                {t("detailPersons") || "Persons"} ({booking.total_persons})
              </div>
              <div className="mb-persons-list">
                {persons.map((p, idx) => (
                  <div key={idx} className="mb-person-chip">
                    <span className="mb-person-num">{idx + 1}</span>
                    <span className="mb-person-name">{p.name}</span>
                    {p.nakshathram && <span className="mb-person-naksh">{p.nakshathram}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer close */}
        <div className="mb-modal-foot">
          <button className="mb-close-btn" onClick={onClose}>
            <div className="mb-close-shimmer" />
            <FaTimes style={{ fontSize: 11, position: "relative", zIndex: 1 }} />
            <span style={{ position: "relative", zIndex: 1 }}>{t("close") || "Close"}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────────────────
   BOOKING CARD — Vertical grid card, 4-5 per row
───────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────
   DONATION CARD
───────────────────────────────────────────────────── */
function DonationCard({ donation, index, onView }) {
  const { t } = useTranslation();
  const statusCfg = {
    success: { label: t("statPaid") || "Paid",    color: "#2d7a2d", bg: "rgba(45,122,45,0.10)",   dot: "#3cb83c", glow: "rgba(45,122,45,0.35)" },
    pending: { label: "Pending", color: "#9a6e00", bg: "rgba(201,151,58,0.12)", dot: "#c9973a", glow: "rgba(201,151,58,0.4)"  },
    failed:  { label: "Failed",  color: "#8b1a1a", bg: "rgba(139,26,26,0.10)",  dot: "#c0392b", glow: "rgba(139,26,26,0.35)" },
  };
  const sc = statusCfg[donation.payment_status] || statusCfg.pending;
  const isOnline = donation.booked_by === "online";
  const d = new Date(donation.created_at);
  const day = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase();
  const yr  = d.getFullYear();
  const amount = parseFloat(donation.amount || 0).toLocaleString("en-IN");
  const initial = donation.donation_type ? donation.donation_type.charAt(0).toUpperCase() : "D";

  return (
    <div className="mb-card" style={{ animationDelay: `${index * 0.06}s` }} onClick={() => onView(donation)}>
      <div className="mb-card-side-bar" style={{ background: `linear-gradient(180deg, ${sc.dot}22 0%, ${sc.dot}ee 40%, ${sc.dot}ee 60%, ${sc.dot}22 100%)` }} />
      <div className="mb-card-inner">
        <div className="mb-card-header">
          <div className="mb-card-header-noise" />
          <div className="mb-card-header-top">
            <div className="mb-card-date-chip">
              <span className="mb-cdc-num">{day} {mon}</span>
              <span className="mb-cdc-yr">{yr}</span>
            </div>
            <span className="mb-card-status-badge" style={{ color: sc.color, background: sc.bg, borderColor: `${sc.dot}55` }}>
              <span className="mb-ct-dot" style={{ background: sc.dot, boxShadow: `0 0 5px ${sc.glow}` }} />
              {sc.label}
            </span>
          </div>
          <div className="mb-card-header-main">
            <div className="mb-card-avatar">
              <div className="mb-card-avatar-ring" />
              <span className="mb-card-avatar-letter">{initial}</span>
            </div>
            <h3 className="mb-card-cat">{donation.donation_type || "Donation"}</h3>
          </div>
          <div className="mb-card-receipt-row">
            <FaFileInvoice className="mb-receipt-icon" />
            <span className="mb-card-receipt-val">{donation.receipt_number || "—"}</span>
          </div>
        </div>
        <div className="mb-card-divider">
          <span className="mb-card-divider-dot" /><span className="mb-card-divider-dot" />
          <span className="mb-card-divider-dot large" />
          <span className="mb-card-divider-dot" /><span className="mb-card-divider-dot" />
        </div>
        <div className="mb-card-body">
          <div className="mb-card-amount-zone">
            <span className="mb-card-amount-lbl">{t("donAmount") || "Amount"}</span>
            <span className="mb-card-amount">&#8377;{amount}</span>
          </div>
          <div className="mb-card-data-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="mb-card-data-item">
              <FaUser className="mb-cdi-icon" />
              <span className="mb-cdi-label">{t("donDonorName") || "Donor"}</span>
              <span className="mb-cdi-value" style={{ fontSize: 12 }}>{donation.name}</span>
            </div>
            <div className="mb-card-data-item">
              {isOnline ? <FaGlobe className="mb-cdi-icon" /> : <FaMoneyBillWave className="mb-cdi-icon" />}
              <span className="mb-cdi-label">{t("tableSource") || "Source"}</span>
              <span className="mb-cdi-value">{isOnline ? "Online" : "Counter"}</span>
            </div>
          </div>
        </div>
        <div className="mb-card-footer">
          <button className="mb-card-view-btn" onClick={e => { e.stopPropagation(); onView(donation); }}>
            <span className="mb-btn-shimmer" />
            <FaEye className="mb-btn-icon" />
            <span className="mb-btn-label">{t("view") || "View Details"}</span>
            <span className="mb-btn-arrow">&#8594;</span>
          </button>
        </div>
      </div>
      <div className="mb-card-hover-glow" />
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   DONATION DETAIL MODAL
───────────────────────────────────────────────────── */
function DonationDetailModal({ donation, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!donation) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, donation]);
  if (!donation) return null;
  const isOnline = donation.booked_by === "online";
  const statusCfg = {
    success: { color: "#2a6b2a", bg: "rgba(42,107,42,0.10)", icon: <FaCheckCircle /> },
    pending: { color: "#c9973a", bg: "rgba(201,151,58,0.10)", icon: <FaHourglassHalf /> },
    failed:  { color: "#8b1a1a", bg: "rgba(139,26,26,0.10)", icon: <FaTimesCircle /> },
  };
  const sc = statusCfg[donation.payment_status] || statusCfg.pending;

  return createPortal(
    <div className="mb-overlay" onClick={onClose}>
      <div className="mb-modal" onClick={e => e.stopPropagation()}>
        <div className="mb-modal-strip" />
        <span className="mb-mc tl" /><span className="mb-mc tr" />
        <span className="mb-mc bl" /><span className="mb-mc br" />
        <div className="mb-modal-head">
          <div className="mb-mh-bg" />
          <div className="mb-mh-inner">
            <div className="mb-mh-icon-wrap">
              <div className="mb-mh-icon-ring" />
              <FaMoneyBillWave className="mb-mh-icon" />
            </div>
            <span className="mb-mh-eyebrow">&#10022; {t("donModalTitle") || "Donation Details"} &#10022;</span>
            <h2 className="mb-mh-receipt">{donation.receipt_number || "—"}</h2>
            <p className="mb-mh-cat">{donation.donation_type}</p>
            <div className="mb-mh-badges">
              <span className={`mb-source-badge ${isOnline ? "online" : "offline"}`}>
                {isOnline ? <FaGlobe /> : <FaMoneyBillWave />}
                {isOnline ? (t("sourceOnline") || "Online") : (t("sourceCounter") || "Counter")}
              </span>
            </div>
          </div>
          <button className="mb-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="mb-modal-rule"><span className="mb-rule-gem">&#10070;</span></div>
        <div className="mb-modal-body">
          <div className="mb-section-label">{t("summary") || "Summary"}</div>
          <div className="mb-detail-grid">
            <div className="mb-di">
              <div className="mb-di-icon"><FaCalendarAlt /></div>
              <div>
                <div className="mb-di-label">{t("donDate") || "Donation Date"}</div>
                <div className="mb-di-val">{new Date(donation.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon"><FaTags /></div>
              <div>
                <div className="mb-di-label">{t("donType") || "Donation Type"}</div>
                <div className="mb-di-val">{donation.donation_type}</div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon"><FaCreditCard /></div>
              <div>
                <div className="mb-di-label">{t("detailTotalPaid") || "Amount"}</div>
                <div className="mb-di-val" style={{ color: "#2a6b2a", fontWeight: 700 }}>&#8377;{parseFloat(donation.amount || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon" style={{ color: sc.color }}>{sc.icon}</div>
              <div>
                <div className="mb-di-label">{t("tableStatus") || "Status"}</div>
                <div className="mb-di-val" style={{ color: sc.color, textTransform: "capitalize" }}>{donation.payment_status}</div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon"><FaUser /></div>
              <div>
                <div className="mb-di-label">{t("donDonorName") || "Donor Name"}</div>
                <div className="mb-di-val">{donation.name}</div>
              </div>
            </div>
            <div className="mb-di">
              <div className="mb-di-icon">{isOnline ? <FaGlobe /> : <FaMoneyBillWave />}</div>
              <div>
                <div className="mb-di-label">{t("tableSource") || "Via"}</div>
                <div className="mb-di-val">{isOnline ? "Online" : "Counter"}</div>
              </div>
            </div>
          </div>
          {donation.address && (
            <>
              <div className="mb-section-label" style={{ marginTop: 20 }}>{t("donAddress") || "Address"}</div>
              <div className="mb-di" style={{ marginTop: 4 }}>
                <div className="mb-di-icon"><FaUserCircle /></div>
                <div><div className="mb-di-val">{donation.address}</div></div>
              </div>
            </>
          )}
          {donation.remarks && (
            <>
              <div className="mb-section-label" style={{ marginTop: 20 }}>{t("donRemarks") || "Remarks"}</div>
              <div className="mb-di" style={{ marginTop: 4 }}>
                <div className="mb-di-icon"><FaFileInvoice /></div>
                <div><div className="mb-di-val" style={{ fontStyle: "italic" }}>{donation.remarks}</div></div>
              </div>
            </>
          )}
        </div>
        <div className="mb-modal-foot">
          <button className="mb-close-btn" onClick={onClose}>
            <div className="mb-close-shimmer" />
            <FaTimes style={{ fontSize: 11, position: "relative", zIndex: 1 }} />
            <span style={{ position: "relative", zIndex: 1 }}>{t("close") || "Close"}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function BookingCard({ booking, index, onView }) {
  const { t } = useTranslation();
  const isOnline = booking.booked_by === "online";

  const statusCfg = {
    success: { label: t("statPaid") || "Paid",    color: "#2d7a2d", bg: "rgba(45,122,45,0.10)",   dot: "#3cb83c", glow: "rgba(45,122,45,0.35)" },
    pending: { label: "Pending", color: "#9a6e00", bg: "rgba(201,151,58,0.12)", dot: "#c9973a", glow: "rgba(201,151,58,0.4)"  },
    failed:  { label: "Failed",  color: "#8b1a1a", bg: "rgba(139,26,26,0.10)",  dot: "#c0392b", glow: "rgba(139,26,26,0.35)" },
  };
  const sc = statusCfg[booking.payment_status] || statusCfg.pending;

  const d = new Date(booking.booking_date);
  const day = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase();
  const yr  = d.getFullYear();
  const amount = parseFloat(booking.amount || booking.unit_price || 0).toLocaleString("en-IN");
  const initial = booking.category_name ? booking.category_name.charAt(0).toUpperCase() : "V";

  return (
    <div className="mb-card" style={{ animationDelay: `${index * 0.06}s` }} onClick={() => onView(booking)}>

      {/* Left status accent bar */}
      <div className="mb-card-side-bar" style={{ background: `linear-gradient(180deg, ${sc.dot}22 0%, ${sc.dot}ee 40%, ${sc.dot}ee 60%, ${sc.dot}22 100%)` }} />

      {/* Inner wrapper offset for side bar */}
      <div className="mb-card-inner">

        {/* ── HEADER ZONE ── */}
        <div className="mb-card-header">
          <div className="mb-card-header-noise" />

          {/* Top meta row: date + status badge */}
          <div className="mb-card-header-top">
            <div className="mb-card-date-chip">
              <span className="mb-cdc-num">{day} {mon}</span>
              <span className="mb-cdc-yr">{yr}</span>
            </div>
            <span className="mb-card-status-badge" style={{ color: sc.color, background: sc.bg, borderColor: `${sc.dot}55` }}>
              <span className="mb-ct-dot" style={{ background: sc.dot, boxShadow: `0 0 5px ${sc.glow}` }} />
              {sc.label}
            </span>
          </div>

          {/* Category + avatar row */}
          <div className="mb-card-header-main">
            <div className="mb-card-avatar">
              <div className="mb-card-avatar-ring" />
              <span className="mb-card-avatar-letter">{initial}</span>
            </div>
            <h3 className="mb-card-cat">{booking.category_name}</h3>
          </div>

          {/* Receipt row */}
          <div className="mb-card-receipt-row">
            <FaFileInvoice className="mb-receipt-icon" />
            <span className="mb-card-receipt-val">{booking.receipt_number}</span>
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div className="mb-card-divider">
          <span className="mb-card-divider-dot" />
          <span className="mb-card-divider-dot" />
          <span className="mb-card-divider-dot large" />
          <span className="mb-card-divider-dot" />
          <span className="mb-card-divider-dot" />
        </div>

        {/* ── BODY ZONE ── */}
        <div className="mb-card-body">

          {/* Amount — the hero number */}
          <div className="mb-card-amount-zone">
            <span className="mb-card-amount-lbl">Total Paid</span>
            <span className="mb-card-amount">₹{amount}</span>
          </div>

          {/* Data grid — persons, mode, source */}
          <div className="mb-card-data-grid">
            <div className="mb-card-data-item">
              <FaUser className="mb-cdi-icon" />
              <div>
                <span className="mb-cdi-label">Persons</span>
                <span className="mb-cdi-value">{booking.total_persons || 1}</span>
              </div>
            </div>
            {booking.payment_mode && booking.payment_mode !== "pending" && (
              <div className="mb-card-data-item">
                <FaCreditCard className="mb-cdi-icon" />
                <div>
                  <span className="mb-cdi-label">Mode</span>
                  <span className="mb-cdi-value">{booking.payment_mode.toUpperCase()}</span>
                </div>
              </div>
            )}
            <div className="mb-card-data-item">
              {isOnline ? <FaGlobe className="mb-cdi-icon" /> : <FaMoneyBillWave className="mb-cdi-icon" />}
              <div>
                <span className="mb-cdi-label">Source</span>
                <span className="mb-cdi-value">{isOnline ? "Online" : "Counter"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div className="mb-card-footer">
          <button className="mb-card-view-btn" onClick={e => { e.stopPropagation(); onView(booking); }}>
            <span className="mb-btn-shimmer" />
            <FaEye className="mb-btn-icon" />
            <span className="mb-btn-label">{t("view") || "View Details"}</span>
            <span className="mb-btn-arrow">→</span>
          </button>
        </div>

      </div>

      {/* Hover glow */}
      <div className="mb-card-hover-glow" />
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   MAIN — MyBookings
───────────────────────────────────────────────────── */
export default function MyBookings() {
  const navigate  = useNavigate();
  const { t }     = useTranslation();
  const [authChecked, setAuthChecked] = useState(false);

  const [bookings,       setBookings]       = useState([]);
  const [filtered,       setFiltered]       = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState("");
  const [specificDate,   setSpecificDate]   = useState("");
  const [dateRange,      setDateRange]      = useState({ start: "", end: "" });
  const [selectedMonth,  setSelectedMonth]  = useState("");
  const [selectedYear,   setSelectedYear]   = useState("");
  const [selectedCat,    setSelectedCat]    = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showFilter,     setShowFilter]     = useState(false);
  const [toast,          setToast]          = useState(null);
  const [activeBooking,  setActiveBooking]  = useState(null);
  const [logoFace,       setLogoFace]       = useState("om");
  const [activeTab,      setActiveTab]      = useState("vazhipadu");
  const [donations,      setDonations]      = useState([]);
  const [donationsLoading, setDonationsLoading] = useState(false);
  const [activeDonation, setActiveDonation] = useState(null);
  // Donation filter state
  const [donSearch,        setDonSearch]        = useState("");
  const [donStatus,        setDonStatus]        = useState("all");
  const [donSource,        setDonSource]        = useState("all");
  const [donType,          setDonType]          = useState("all");
  const [donSpecificDate,  setDonSpecificDate]  = useState("");
  const [donDateRange,     setDonDateRange]     = useState({ start: "", end: "" });
  const [donMonth,         setDonMonth]         = useState("");
  const [donYear,          setDonYear]          = useState("");
  const [donShowFilter,    setDonShowFilter]    = useState(false);
  const [donFiltered,      setDonFiltered]      = useState([]);

  const searchRef = useRef(null);

  // Medallion rotate
  useEffect(() => {
    const id = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(id);
  }, []);

   useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("authToken");

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);

        // If somehow an admin lands on /home, redirect them to /admin
        if (parsedUser.role === 'admin') {
          window.location.href = '/admin';
          return;
        }

        setAuthChecked(true); // ✅ valid devotee — show the page

      } catch (e) {
        localStorage.clear();
        window.location.href = '/login';
      }
    } else {
      // No session — send to login
      window.location.href = '/login';
    }
  }, []);

  const showToast = (message, type = "info") => setToast({ message, type });

  // ── FETCH CATEGORIES ──
  const fetchCategories = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/api/admin/vazhipadu-categories`);
      const data = await res.json();
      setCategories(data || []);
    } catch { /* silent */ }
  }, [API]);

  // ── FETCH MY BOOKINGS ──
  const fetchBookings = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("authToken");
    if (!token) { navigate("/login"); return; }
    try {
      const res = await fetch(`${API}/api/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { navigate("/login"); return; }
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setBookings(data);
      setFiltered(data);
    } catch {
      showToast(t("toastFetchFailed") || "Failed to load bookings.", "error");
    } finally {
      setLoading(false);
    }
  }, [API, navigate, t]);

  // ── FETCH MY DONATIONS ──
  const fetchDonations = useCallback(async () => {
    setDonationsLoading(true);
    const token = localStorage.getItem("authToken");
    if (!token) { navigate("/login"); return; }
    try {
      const res = await fetch(`${API}/api/my-donations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { navigate("/login"); return; }
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setDonations(data);
    } catch {
      showToast(t("donToastFetchFailed") || "Failed to load donations.", "error");
    } finally {
      setDonationsLoading(false);
    }
  }, [API, navigate, t]);

  useEffect(() => { fetchCategories(); fetchBookings(); fetchDonations(); }, [fetchCategories, fetchBookings, fetchDonations]);

  // Sync donFiltered when donations loads
  useEffect(() => { setDonFiltered(donations); }, [donations]);

  // Donation filter logic
  useEffect(() => {
    let r = [...donations];
    if (donSearch.trim()) {
      const q = donSearch.toLowerCase();
      r = r.filter(d =>
        d.receipt_number?.toLowerCase().includes(q) ||
        d.donation_type?.toLowerCase().includes(q) ||
        d.name?.toLowerCase().includes(q)
      );
    }
    if (donStatus !== "all") r = r.filter(d => d.payment_status === donStatus);
    if (donSource !== "all") r = r.filter(d => donSource === "online" ? d.booked_by === "online" : d.booked_by === "admin");
    if (donType   !== "all") r = r.filter(d => d.donation_type === donType);
    if (donSpecificDate) {
      r = r.filter(d => String(d.created_at).slice(0,10) === donSpecificDate);
    } else if (donDateRange.start || donDateRange.end) {
      r = r.filter(d => {
        const dt = String(d.created_at).slice(0,10);
        if (donDateRange.start && donDateRange.end) return dt >= donDateRange.start && dt <= donDateRange.end;
        if (donDateRange.start) return dt >= donDateRange.start;
        if (donDateRange.end)   return dt <= donDateRange.end;
        return true;
      });
    } else if (donMonth) {
      r = r.filter(d => String(d.created_at).slice(0,10).startsWith(donMonth));
    } else if (donYear) {
      r = r.filter(d => String(d.created_at).slice(0,10).startsWith(donYear));
    }
    setDonFiltered(r);
  }, [donations, donSearch, donStatus, donSource, donType, donSpecificDate, donDateRange, donMonth, donYear]);

  const clearDonFilters = () => {
    setDonSearch(""); setDonStatus("all"); setDonSource("all"); setDonType("all");
    setDonSpecificDate(""); setDonDateRange({ start: "", end: "" });
    setDonMonth(""); setDonYear("");
  };
  const donActiveFilterCount =
    (donSearch ? 1 : 0) + (donStatus !== "all" ? 1 : 0) + (donSource !== "all" ? 1 : 0) +
    (donType !== "all" ? 1 : 0) + (donSpecificDate ? 1 : 0) +
    (donDateRange.start || donDateRange.end ? 1 : 0) + (donMonth ? 1 : 0) + (donYear ? 1 : 0);
  // Unique donation types for filter dropdown
  const donationTypes = [...new Set(donations.map(d => d.donation_type).filter(Boolean))];

  // ── FILTER LOGIC ──
  const toDateStr = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    if (!s.endsWith("Z")) return s.slice(0, 10);
    const d = new Date(s);
    if (isNaN(d)) return s.slice(0, 10);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  useEffect(() => {
    let r = [...bookings];

    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(b =>
        b.receipt_number?.toLowerCase().includes(q) ||
        b.category_name?.toLowerCase().includes(q)
      );
    }

    if (selectedCat !== "all") {
      r = r.filter(b => String(b.category_name).toLowerCase() === String(selectedCat).toLowerCase());
    }

    if (selectedSource !== "all") {
      r = r.filter(b => selectedSource === "online" ? b.booked_by === "online" : b.booked_by === "admin");
    }

    if (selectedStatus !== "all") {
      r = r.filter(b => b.payment_status === selectedStatus);
    }

    if (specificDate) {
      r = r.filter(b => toDateStr(b.booking_date) === specificDate);
    } else if (dateRange.start || dateRange.end) {
      r = r.filter(b => {
        const d = toDateStr(b.booking_date);
        if (dateRange.start && dateRange.end) return d >= dateRange.start && d <= dateRange.end;
        if (dateRange.start) return d >= dateRange.start;
        if (dateRange.end)   return d <= dateRange.end;
        return true;
      });
    } else if (selectedMonth) {
      r = r.filter(b => toDateStr(b.booking_date).startsWith(selectedMonth));
    } else if (selectedYear) {
      r = r.filter(b => toDateStr(b.booking_date).startsWith(selectedYear));
    }

    setFiltered(r);
  }, [search, selectedCat, selectedSource, selectedStatus, specificDate, dateRange, selectedMonth, selectedYear, bookings]);

  // ── HANDLERS ──
  const handleSpecificDate = (val) => {
    setSpecificDate(val);
    setSelectedMonth("");
    setSelectedYear("");
    if (val) setDateRange({ start: "", end: "" });
  };

  const handleRange = (field, val) => {
    setDateRange(prev => ({ ...prev, [field]: val }));
    setSpecificDate("");
    setSelectedMonth("");
    setSelectedYear("");
  };

  const handleMonth = (val) => {
    setSelectedMonth(val);
    setSpecificDate("");
    setSelectedYear("");
    setDateRange({ start: "", end: "" });
  };

  const handleYear = (val) => {
    setSelectedYear(val);
    setSpecificDate("");
    setSelectedMonth("");
    setDateRange({ start: "", end: "" });
  };

  const clearAll = () => {
    setSearch(""); setSpecificDate(""); setDateRange({ start: "", end: "" });
    setSelectedMonth(""); setSelectedYear("");
    setSelectedCat("all"); setSelectedSource("all"); setSelectedStatus("all");
  };

  const activeFilterCount =
    (search ? 1 : 0) +
    (selectedCat !== "all" ? 1 : 0) +
    (selectedSource !== "all" ? 1 : 0) +
    (selectedStatus !== "all" ? 1 : 0) +
    (specificDate ? 1 : 0) +
    (dateRange.start || dateRange.end ? 1 : 0) +
    (selectedMonth ? 1 : 0) +
    (selectedYear ? 1 : 0);

  // Year range for select
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Stats — reactive to active tab
  const activeData = activeTab === "donations" ? donations : bookings;
  const amountField = (item) => parseFloat(
    activeTab === "donations" ? (item.amount || 0) : (item.amount || item.unit_price || 0)
  );
  const totalPaid    = activeData.reduce((s, b) => b.payment_status === "success" ? s + amountField(b) : s, 0);
  const successCount = activeData.filter(b => b.payment_status === "success").length;
  const pendingCount = activeData.filter(b => b.payment_status === "pending").length;
  const totalCount   = activeData.length;
  
  if (!authChecked) return null;

  return (
    <>
      <MbStyles />
      <Navbar />

      {/* ── HERO ── */}
      <div className="mb-hero">
        <div className="mb-hero-geo mb-geo-1" />
        <div className="mb-hero-geo mb-geo-2" />
        <div className="mb-hero-geo mb-geo-3" />
        <div className="mb-hero-scanlines" />

        <div className="mb-hero-left-col">
          <div className="mb-hlc-top-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="mb-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="mb-hlc-om">ॐ</div>
          <div className="mb-hlc-line" />
          <span className="mb-hlc-symbol">◈</span>
          <div className="mb-hlc-line" />
          <div className="mb-hlc-bot-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="mb-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>

        <div className="mb-hero-center">
          <div className="mb-hero-eyebrow">
            <span className="mb-ey-dash" />
            <span className="mb-ey-text">{t("myBookingsEyebrow") || "Devotee Portal"}</span>
            <span className="mb-ey-dash" />
          </div>
          <h1 className="mb-hero-title">{t("myBookingsTitle") || "My Bookings"}</h1>
          <p className="mb-hero-sub">{t("myBookingsSub") || "Your sacred activity history"}</p>

          {/* TAB SELECTOR */}
          <div className="mb-tab-selector">
            <button className={`mb-tab-btn ${activeTab === "vazhipadu" ? "active" : ""}`} onClick={() => setActiveTab("vazhipadu")}>
              <FaTags className="mb-tab-icon" />
              <span className="mb-tab-label">{t("tabVazhipadu") || "Vazhipadukal"}</span>
              {bookings.length > 0 && <span className="mb-tab-count">{bookings.length}</span>}
            </button>
            <button className={`mb-tab-btn ${activeTab === "donations" ? "active" : ""}`} onClick={() => setActiveTab("donations")}>
              <FaMoneyBillWave className="mb-tab-icon" />
              <span className="mb-tab-label">{t("tabDonations") || "Donations"}</span>
              {donations.length > 0 && <span className="mb-tab-count">{donations.length}</span>}
            </button>
          </div>
          <div className="mb-stat-row" key={activeTab}>
            <div className="mb-stat">
              <span className="mb-stat-val mb-stat-animate" style={{ "--sv": "#e8c46a" }}>{totalCount}</span>
              <span className="mb-stat-lbl">{t("statTotal") || "Total"}</span>
            </div>
            <div className="mb-stat-divider" />
            <div className="mb-stat">
              <span className="mb-stat-val mb-stat-animate" style={{ "--sv": "#7dcf7d", animationDelay: "0.05s" }}>{successCount}</span>
              <span className="mb-stat-lbl">{t("statPaid") || "Completed"}</span>
            </div>
            <div className="mb-stat-divider" />
            <div className="mb-stat">
              <span className="mb-stat-val mb-stat-animate" style={{ "--sv": "#e8a46a", animationDelay: "0.1s" }}>{pendingCount}</span>
              <span className="mb-stat-lbl">{t("statPending") || "Pending"}</span>
            </div>
            <div className="mb-stat-divider" />
            <div className="mb-stat">
              <span className="mb-stat-val mb-stat-animate" style={{ "--sv": "#f0d080", fontSize: "1rem", animationDelay: "0.15s" }}>₹{totalPaid.toLocaleString("en-IN")}</span>
              <span className="mb-stat-lbl">{t("statSpent") || "Total Paid"}</span>
            </div>
          </div>
        </div>

        <div className="mb-hero-right-col">
          <div className="mb-hlc-top-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="mb-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="mb-hlc-om">ॐ</div>
          <div className="mb-hlc-line" />
          <span className="mb-hlc-symbol">◈</span>
          <div className="mb-hlc-line" />
          <div className="mb-hlc-bot-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="mb-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>

        <div className="mb-hero-bottom-strip" />
      </div>

{activeTab === "vazhipadu" && <>
      {/* ── TOOLBAR ── */}
      <div className="mb-toolbar">
        <div className="mb-search-wrap">
          <FaSearch className="mb-search-icon" />
          <input
            ref={searchRef}
            className="mb-search-input"
            placeholder={t("searchPlaceholder") || "Search receipt, vazhipadu…"}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="mb-search-clear" onClick={() => setSearch("")}><FaTimes /></button>}
        </div>

        <button className="mb-filter-btn" onClick={() => setShowFilter(v => !v)}>
          <FaFilter />
          <span>{t("btnFilters") || "Filters"}</span>
          {activeFilterCount > 0 && <span className="mb-filter-badge">{activeFilterCount}</span>}
          <FaChevronDown className={`mb-chevron ${showFilter ? "open" : ""}`} />
        </button>

        <div className="mb-toolbar-end">
          {activeFilterCount > 0 && (
            <button className="mb-action-btn mb-btn-clear" onClick={clearAll}>
              <FaTimes /> {t("btnClear") || "Clear"}
            </button>
          )}
          <button className="mb-action-btn mb-btn-refresh" onClick={fetchBookings}>
            <FaSync className={loading ? "spin" : ""} /> {t("btnRefresh") || "Refresh"}
          </button>
        </div>
      </div>

      {/* ── FILTER PANEL ── */}
      {showFilter && (
        <div className="mb-filter-panel">
          <div className="mb-fp-strip" />
          <div className="mb-filter-grid">

            {/* Source */}
            <div className="mb-fi">
              <label className="mb-fi-label"><FaGlobe /> {t("filterSource") || "Source"}</label>
              <select className="mb-fi-select" value={selectedSource} onChange={e => setSelectedSource(e.target.value)}>
                <option value="all">{t("filterSourceAll") || "All Sources"}</option>
                <option value="online">{t("filterSourceOnline") || "Online"}</option>
                <option value="offline">{t("filterSourceOffline") || "Counter"}</option>
              </select>
            </div>

            {/* Category */}
            <div className="mb-fi">
              <label className="mb-fi-label"><FaTags /> {t("filterCategory") || "Vazhipadu"}</label>
              <select className="mb-fi-select" value={selectedCat} onChange={e => setSelectedCat(e.target.value)}>
                <option value="all">{t("filterCategoryAll") || "All Vazhipadus"}</option>
                {categories.map(c => (
                  <option key={c.vazhipadu_id} value={c.name}>{c.name} — ₹{c.amount}</option>
                ))}
              </select>
            </div>

            {/* Payment Status */}
            <div className="mb-fi">
              <label className="mb-fi-label"><FaCheckCircle /> {t("tableStatus") || "Status"}</label>
              <select className="mb-fi-select" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="success">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Specific Date */}
            <div className="mb-fi">
              <label className="mb-fi-label"><FaCalendarAlt /> {t("filterSpecificDate") || "Specific Date"}</label>
              <input
                type="date" className="mb-fi-input"
                value={specificDate}
                onChange={e => handleSpecificDate(e.target.value)}
              />
              {specificDate && (
                <button className="mb-fi-clear" onClick={() => setSpecificDate("")}>
                  {t("filterClearDate") || "Clear date"}
                </button>
              )}
            </div>

            {/* Date Range */}
            <div className={`mb-fi mb-fi-range ${specificDate ? "mb-fi-disabled" : ""}`}>
              <label className="mb-fi-label">
                <FaCalendarAlt /> {t("filterDateRange") || "Date Range"}
                {specificDate && <span className="mb-fi-note"> (clear date first)</span>}
              </label>
              <div className="mb-range-row">
                <input
                  type="date" className="mb-fi-input"
                  value={dateRange.start}
                  onChange={e => handleRange("start", e.target.value)}
                  disabled={!!specificDate}
                />
                <span className="mb-range-sep">→</span>
                <input
                  type="date" className="mb-fi-input"
                  value={dateRange.end}
                  onChange={e => handleRange("end", e.target.value)}
                  disabled={!!specificDate}
                />
              </div>
              {(dateRange.start || dateRange.end) && !specificDate && (
                <button className="mb-fi-clear" onClick={() => setDateRange({ start: "", end: "" })}>
                  {t("filterClearRange") || "Clear range"}
                </button>
              )}
            </div>

            {/* Month */}
            <div className={`mb-fi ${(specificDate || dateRange.start || dateRange.end) ? "mb-fi-disabled" : ""}`}>
              <label className="mb-fi-label">
                <FaCalendarAlt /> {t("filterMonth") || "Month"}
                {(specificDate || dateRange.start || dateRange.end) && <span className="mb-fi-note"> (clear date filters first)</span>}
              </label>
              <input
                type="month" className="mb-fi-input"
                value={selectedMonth}
                onChange={e => handleMonth(e.target.value)}
                disabled={!!(specificDate || dateRange.start || dateRange.end)}
              />
              {selectedMonth && !(specificDate || dateRange.start || dateRange.end) && (
                <button className="mb-fi-clear" onClick={() => setSelectedMonth("")}>
                  {t("filterClearMonth") || "Clear month"}
                </button>
              )}
            </div>

            {/* Year */}
            <div className={`mb-fi ${(specificDate || dateRange.start || dateRange.end || selectedMonth) ? "mb-fi-disabled" : ""}`}>
              <label className="mb-fi-label">
                <FaCalendarAlt /> {t("filterYear") || "Year"}
                {(specificDate || dateRange.start || dateRange.end || selectedMonth) && <span className="mb-fi-note"> (clear other filters first)</span>}
              </label>
              <select
                className="mb-fi-select"
                value={selectedYear}
                onChange={e => handleYear(e.target.value)}
                disabled={!!(specificDate || dateRange.start || dateRange.end || selectedMonth)}
              >
                <option value="">All Years</option>
                {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
              </select>
            </div>

          </div>
        </div>
      )}

      {/* ── RESULT INFO ── */}
      <div className="mb-result-info">
        {!loading && (
          <span>
            {t("displaying") || "Showing"} <strong>{filtered.length}</strong> {t("bookings") || "booking(s)"}
            {activeFilterCount > 0 && (
              <button className="mb-clear-inline" onClick={clearAll}>
                <FaTimes /> {t("clearAll") || "Clear all"}
              </button>
            )}
          </span>
        )}
      </div>

      {/* ── CONTENT ── */}
      <div className="mb-content">
        {loading ? (
          <div className="mb-loader">
            <div className="mb-loader-ring-wrap">
              <div className="mb-loader-ring" />
              <div className="mb-loader-ring-inner" />
              <div className="mb-loader-om">ॐ</div>
            </div>
            <p>{t("loading") || "Loading your bookings…"}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mb-empty">
            <div className="mb-empty-glyph">📿</div>
            <h3 className="mb-empty-title">
              {bookings.length === 0
                ? (t("noBookingsYet") || "No bookings yet")
                : (t("emptyTitle") || "No matching bookings")}
            </h3>
            <p className="mb-empty-sub">
              {bookings.length === 0
                ? (t("noBookingsSub") || "Your vazhipadu bookings will appear here.")
                : (t("emptySub") || "Try adjusting your filters.")}
            </p>
            {activeFilterCount > 0 && (
              <button className="mb-empty-clear" onClick={clearAll}>
                <FaTimes /> {t("btnClearFilters") || "Clear Filters"}
              </button>
            )}
          </div>
        ) : (
          <div className="mb-card-grid">
            {filtered.map((b, i) => (
              <BookingCard key={b.booking_id} booking={b} index={i} onView={setActiveBooking} />
            ))}
          </div>
        )}
      </div>

      {/* ── MODALS & TOAST ── */}
      {activeBooking && (
        <BookingDetailModal booking={activeBooking} onClose={() => setActiveBooking(null)} />
      )}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      </> /* vazhipadu tab end */}

      {/* ── DONATIONS TAB ── */}
      {activeTab === "donations" && <>

        {/* Donation Toolbar */}
        <div className="mb-toolbar">
          <div className="mb-search-wrap">
            <FaSearch className="mb-search-icon" />
            <input
              className="mb-search-input"
              placeholder={t("donSearchPlaceholder") || "Search receipt, type, name…"}
              value={donSearch}
              onChange={e => setDonSearch(e.target.value)}
            />
            {donSearch && <button className="mb-search-clear" onClick={() => setDonSearch("")}><FaTimes /></button>}
          </div>
          <button className="mb-filter-btn" onClick={() => setDonShowFilter(v => !v)}>
            <FaFilter />
            <span>{t("btnFilters") || "Filters"}</span>
            {donActiveFilterCount > 0 && <span className="mb-filter-badge">{donActiveFilterCount}</span>}
            <FaChevronDown className={`mb-chevron ${donShowFilter ? "open" : ""}`} />
          </button>
          <div className="mb-toolbar-end">
            {donActiveFilterCount > 0 && (
              <button className="mb-action-btn mb-btn-clear" onClick={clearDonFilters}>
                <FaTimes /> {t("btnClear") || "Clear"}
              </button>
            )}
            <button className="mb-action-btn mb-btn-refresh" onClick={fetchDonations}>
              <FaSync className={donationsLoading ? "spin" : ""} /> {t("btnRefresh") || "Refresh"}
            </button>
          </div>
        </div>

        {/* Donation Filter Panel */}
        {donShowFilter && (
          <div className="mb-filter-panel">
            <div className="mb-fp-strip" />
            <div className="mb-filter-grid">

              {/* Donation Type */}
              <div className="mb-fi">
                <label className="mb-fi-label"><FaTags /> {t("donType") || "Type"}</label>
                <select className="mb-fi-select" value={donType} onChange={e => setDonType(e.target.value)}>
                  <option value="all">{t("donTypeAll") || "All Types"}</option>
                  {donationTypes.map(dt => <option key={dt} value={dt}>{dt}</option>)}
                </select>
              </div>

              {/* Status */}
              <div className="mb-fi">
                <label className="mb-fi-label"><FaCheckCircle /> {t("tableStatus") || "Status"}</label>
                <select className="mb-fi-select" value={donStatus} onChange={e => setDonStatus(e.target.value)}>
                  <option value="all">All Statuses</option>
                  <option value="success">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* Source */}
              <div className="mb-fi">
                <label className="mb-fi-label"><FaGlobe /> {t("filterSource") || "Source"}</label>
                <select className="mb-fi-select" value={donSource} onChange={e => setDonSource(e.target.value)}>
                  <option value="all">{t("filterSourceAll") || "All Sources"}</option>
                  <option value="online">{t("filterSourceOnline") || "Online"}</option>
                  <option value="offline">{t("filterSourceOffline") || "Counter"}</option>
                </select>
              </div>

              {/* Specific Date */}
              <div className="mb-fi">
                <label className="mb-fi-label"><FaCalendarAlt /> {t("filterSpecificDate") || "Specific Date"}</label>
                <input type="date" className="mb-fi-input" value={donSpecificDate}
                  onChange={e => { setDonSpecificDate(e.target.value); setDonMonth(""); setDonYear(""); if (e.target.value) setDonDateRange({ start: "", end: "" }); }}
                />
                {donSpecificDate && <button className="mb-fi-clear" onClick={() => setDonSpecificDate("")}>{t("filterClearDate") || "Clear date"}</button>}
              </div>

              {/* Date Range */}
              <div className={`mb-fi mb-fi-range ${donSpecificDate ? "mb-fi-disabled" : ""}`}>
                <label className="mb-fi-label"><FaCalendarAlt /> {t("filterDateRange") || "Date Range"}
                  {donSpecificDate && <span className="mb-fi-note"> (clear date first)</span>}
                </label>
                <div className="mb-range-row">
                  <input type="date" className="mb-fi-input" value={donDateRange.start} disabled={!!donSpecificDate}
                    onChange={e => { setDonDateRange(p => ({ ...p, start: e.target.value })); setDonSpecificDate(""); setDonMonth(""); setDonYear(""); }}
                  />
                  <span className="mb-range-sep">→</span>
                  <input type="date" className="mb-fi-input" value={donDateRange.end} disabled={!!donSpecificDate}
                    onChange={e => { setDonDateRange(p => ({ ...p, end: e.target.value })); setDonSpecificDate(""); setDonMonth(""); setDonYear(""); }}
                  />
                </div>
                {(donDateRange.start || donDateRange.end) && !donSpecificDate && (
                  <button className="mb-fi-clear" onClick={() => setDonDateRange({ start: "", end: "" })}>{t("filterClearRange") || "Clear range"}</button>
                )}
              </div>

              {/* Month */}
              <div className={`mb-fi ${(donSpecificDate || donDateRange.start || donDateRange.end) ? "mb-fi-disabled" : ""}`}>
                <label className="mb-fi-label"><FaCalendarAlt /> {t("filterMonth") || "Month"}</label>
                <input type="month" className="mb-fi-input" value={donMonth}
                  disabled={!!(donSpecificDate || donDateRange.start || donDateRange.end)}
                  onChange={e => { setDonMonth(e.target.value); setDonSpecificDate(""); setDonYear(""); setDonDateRange({ start: "", end: "" }); }}
                />
                {donMonth && !(donSpecificDate || donDateRange.start || donDateRange.end) && (
                  <button className="mb-fi-clear" onClick={() => setDonMonth("")}>{t("filterClearMonth") || "Clear month"}</button>
                )}
              </div>

              {/* Year */}
              <div className={`mb-fi ${(donSpecificDate || donDateRange.start || donDateRange.end || donMonth) ? "mb-fi-disabled" : ""}`}>
                <label className="mb-fi-label"><FaCalendarAlt /> {t("filterYear") || "Year"}</label>
                <select className="mb-fi-select" value={donYear}
                  disabled={!!(donSpecificDate || donDateRange.start || donDateRange.end || donMonth)}
                  onChange={e => { setDonYear(e.target.value); setDonSpecificDate(""); setDonMonth(""); setDonDateRange({ start: "", end: "" }); }}
                >
                  <option value="">All Years</option>
                  {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
                </select>
              </div>

            </div>
          </div>
        )}

        {/* Result info */}
        <div className="mb-result-info">
          {!donationsLoading && (
            <span>
              {t("displaying") || "Showing"} <strong>{donFiltered.length}</strong> {t("donRecords") || "donation(s)"}
              {donActiveFilterCount > 0 && (
                <button className="mb-clear-inline" onClick={clearDonFilters}><FaTimes /> {t("clearAll") || "Clear all"}</button>
              )}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="mb-content">
          {donationsLoading ? (
            <div className="mb-loader">
              <div className="mb-loader-ring-wrap">
                <div className="mb-loader-ring" />
                <div className="mb-loader-ring-inner" />
                <div className="mb-loader-om">ॐ</div>
              </div>
              <p>{t("donLoading") || "Loading your donations..."}</p>
            </div>
          ) : donFiltered.length === 0 ? (
            <div className="mb-empty">
              <div className="mb-empty-glyph">🤝</div>
              <h3 className="mb-empty-title">
                {donations.length === 0 ? (t("donNoneYet") || "No donations yet") : (t("emptyTitle") || "No matching donations")}
              </h3>
              <p className="mb-empty-sub">
                {donations.length === 0 ? (t("donNoneSub") || "Your donation history will appear here.") : (t("emptySub") || "Try adjusting your filters.")}
              </p>
              {donActiveFilterCount > 0 && (
                <button className="mb-empty-clear" onClick={clearDonFilters}><FaTimes /> {t("btnClearFilters") || "Clear Filters"}</button>
              )}
            </div>
          ) : (
            <div className="mb-card-grid">
              {donFiltered.map((don, i) => (
                <DonationCard key={don.donation_id} donation={don} index={i} onView={setActiveDonation} />
              ))}
            </div>
          )}
        </div>

        {activeDonation && (
          <DonationDetailModal donation={activeDonation} onClose={() => setActiveDonation(null)} />
        )}
      </>}

      <Footer />
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   STYLES — Premium Temple Editorial Design
═══════════════════════════════════════════════════════ */
function MbStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --crimson-deep:  #1a0404;
        --crimson-mid:   #2c0808;
        --gold-bright:   #e8c46a;
        --gold-mid:      #c9973a;
        --gold-dim:      #8a6020;
        --gold-faint:    rgba(201,151,58,0.15);
        --parchment:     #FDF3E3;
        --parchment-mid: #f8ead4;
        --text-dark:     #1a0404;
        --text-mid:      #4a1a08;
      }
      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

      /* ══════════════════════════════════════════════
         HERO — Wide panoramic, three-column layout
      ══════════════════════════════════════════════ */
      .mb-hero {
        position: relative; overflow: hidden;
        background: linear-gradient(165deg, #0e0101 0%, #1a0404 30%, #2c0808 55%, #1a0404 78%, #0a0000 100%);
        display: grid;
        grid-template-columns: 72px 1fr 72px;
        grid-template-rows: 1fr auto;
        min-height: 240px;
        padding: 0;
      }

      /* Background geometric layers */
      .mb-hero-geo {
        position: absolute; pointer-events: none;
      }
      .mb-geo-1 {
        inset: 0;
        background:
          repeating-linear-gradient(
            -55deg,
            transparent 0px, transparent 40px,
            rgba(201,151,58,0.018) 40px, rgba(201,151,58,0.018) 41px
          );
      }
      .mb-geo-2 {
        width: 500px; height: 500px;
        top: -200px; left: 50%; transform: translateX(-50%);
        background: radial-gradient(ellipse, rgba(120,25,25,0.35) 0%, transparent 65%);
        border-radius: 50%;
      }
      .mb-geo-3 {
        inset: 0;
        background:
          radial-gradient(ellipse 30% 80% at 0% 50%, rgba(201,151,58,0.04) 0%, transparent 100%),
          radial-gradient(ellipse 30% 80% at 100% 50%, rgba(201,151,58,0.04) 0%, transparent 100%);
      }
      .mb-hero-scanlines {
        position: absolute; inset: 0; pointer-events: none;
        background: repeating-linear-gradient(
          0deg, transparent 0px, transparent 3px,
          rgba(0,0,0,0.04) 3px, rgba(0,0,0,0.04) 4px
        );
      }

      /* Side decorative columns */
      .mb-hero-left-col,
      .mb-hero-right-col {
        display: flex; flex-direction: column; align-items: center;
        justify-content: center; gap: 8px; padding: 28px 0;
        position: relative; z-index: 2;
        border-right: 1px solid rgba(201,151,58,0.12);
      }
      .mb-hero-right-col {
        border-right: none;
        border-left: 1px solid rgba(201,151,58,0.12);
      }
      .mb-hlc-top-ornament,
      .mb-hlc-bot-ornament {
        display: flex; flex-direction: column; align-items: center; gap: 2px;
      }
      .mb-hlc-tick {
        width: 2px; border-radius: 1px;
        background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid));
      }
      .mb-hlc-om {
        font-family: 'Cinzel Decorative', cursive; font-size: 20px;
        background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid));
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        filter: drop-shadow(0 0 8px rgba(232,196,106,0.5));
        line-height: 1;
      }
      .mb-hlc-line {
        width: 1px; flex: 1; min-height: 16px;
        background: linear-gradient(180deg, transparent, rgba(201,151,58,0.3), transparent);
      }
      .mb-hlc-face {
        position: absolute;
        display: flex; align-items: center; justify-content: center;
        opacity: 0; transform: scale(0.8);
        transition: opacity 0.5s ease, transform 0.5s ease;
        top: 0; left: 0; right: 0; bottom: 0;
      }
      .mb-hlc-face.hlc-vis { opacity: 1; transform: scale(1); }
      .mb-hlc-switcher {
        position: relative;
        width: 44px; height: 44px;
        display: flex; align-items: center; justify-content: center;
        flex-shrink: 0;
      }
      .mb-hlc-logo { width: 36px; height: 36px; object-fit: contain; filter: drop-shadow(0 0 5px rgba(232,196,106,0.35)); }
      .mb-hlc-symbol { font-size: 16px; color: rgba(232,196,106,0.5); line-height: 1; }
      .mb-hlc-symbol-alt {
        font-size: 14px; color: rgba(232,196,106,0.4); line-height: 1;
        animation: mbPulse 3s ease-in-out infinite alternate;
      }

      /* Center content */
      .mb-hero-center {
        display: flex; flex-direction: column; align-items: center;
        justify-content: center; padding: 40px 32px 32px;
        text-align: center; position: relative; z-index: 2;
      }
      .mb-hero-eyebrow {
        display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
      }
      .mb-ey-dash {
        width: 28px; height: 1px;
        background: linear-gradient(90deg, transparent, rgba(232,196,106,0.5));
      }
      .mb-ey-dash:last-child { background: linear-gradient(90deg, rgba(232,196,106,0.5), transparent); }
      .mb-ey-text {
        font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700;
        letter-spacing: 4px; text-transform: uppercase; color: rgba(232,196,106,0.45);
        white-space: nowrap;
      }
      .mb-hero-title {
        font-family: 'Cinzel Decorative', cursive; font-size: 32px; font-weight: 900;
        background: linear-gradient(160deg, #fffaf0 0%, var(--gold-bright) 35%, #f5d870 55%, var(--gold-mid) 80%, #c07820 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        line-height: 1.25; margin-bottom: 10px;
        filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));
        letter-spacing: -0.5px;
      }
      .mb-hero-sub {
        font-family: 'EB Garamond', serif; font-style: italic; font-size: 16px;
        color: rgba(245,228,176,0.38); margin-bottom: 32px; letter-spacing: 0.3px;
      }

      /* Stat row — inline horizontal inside hero */
      .mb-stat-row {
        display: flex; align-items: center; gap: 0;
        background: rgba(255,255,255,0.03);
        border: 1px solid rgba(201,151,58,0.18);
        overflow: hidden;
      }
      .mb-stat {
        display: flex; flex-direction: column; align-items: center;
        padding: 14px 28px; gap: 4px; position: relative;
        transition: background 0.2s;
      }
      .mb-stat:hover { background: rgba(201,151,58,0.06); }
      .mb-stat-val {
        font-family: 'Cinzel Decorative', cursive; font-size: 1.35rem; font-weight: 900;
        color: var(--sv, var(--gold-bright));
        filter: drop-shadow(0 1px 4px rgba(232,196,106,0.3));
        line-height: 1;
      }
      .mb-stat-lbl {
        font-family: 'Cinzel', serif; font-size: 7px; font-weight: 700;
        letter-spacing: 2.5px; text-transform: uppercase;
        color: rgba(232,196,106,0.28); white-space: nowrap;
      }
      .mb-stat-divider {
        width: 1px; height: 40px; flex-shrink: 0;
        background: linear-gradient(180deg, transparent, rgba(201,151,58,0.25), transparent);
      }

      /* Bottom gold strip */
      .mb-hero-bottom-strip {
        grid-column: 1 / -1;
        height: 3px;
        background: linear-gradient(90deg, transparent 0%, var(--gold-dim) 8%, var(--gold-bright) 28%, #fff8e7 50%, var(--gold-bright) 72%, var(--gold-dim) 92%, transparent 100%);
      }

      @keyframes mbPulse { from { opacity: 0.3; } to { opacity: 0.7; } }
      @keyframes mbSpin  { to { transform: rotate(360deg); } }

      /* ══════════════════════════════════════════════
         TOOLBAR — clean, structured
      ══════════════════════════════════════════════ */
      .mb-toolbar {
        background: #fdf8ef;
        border-bottom: 1px solid rgba(201,151,58,0.18);
        padding: 12px 32px;
        display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
      }

      .mb-search-wrap {
        display: flex; align-items: center; flex: 1; min-width: 220px;
        background: #fff;
        border: 1px solid rgba(201,151,58,0.28);
        border-radius: 2px;
        overflow: hidden;
        transition: border-color 0.2s, box-shadow 0.2s;
      }
      .mb-search-wrap:focus-within {
        border-color: var(--gold-mid);
        box-shadow: 0 0 0 3px rgba(201,151,58,0.10);
      }
      .mb-search-icon { color: var(--gold-dim); font-size: 12px; margin: 0 12px; flex-shrink: 0; }
      .mb-search-input {
        flex: 1; border: none; outline: none; background: transparent;
        font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark);
        padding: 10px 0;
      }
      .mb-search-input::placeholder { color: rgba(74,26,8,0.22); font-style: italic; }
      .mb-search-clear {
        padding: 8px 12px; background: none; border: none; cursor: pointer;
        color: rgba(74,26,8,0.3); font-size: 11px;
        transition: color 0.2s;
      }
      .mb-search-clear:hover { color: #8b1a1a; }

      .mb-filter-btn {
        display: flex; align-items: center; gap: 8px;
        font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px;
        text-transform: uppercase; color: var(--gold-dim);
        background: transparent; border: 1px solid rgba(201,151,58,0.28);
        padding: 9px 16px; cursor: pointer; border-radius: 2px; flex-shrink: 0;
        transition: background 0.2s, border-color 0.2s;
      }
      .mb-filter-btn:hover { background: rgba(201,151,58,0.08); border-color: rgba(201,151,58,0.5); }
      .mb-filter-badge {
        background: var(--gold-mid); color: #1a0404; border-radius: 10px;
        padding: 1px 7px; font-size: 9px; font-weight: 900;
      }
      .mb-chevron { transition: transform 0.3s; font-size: 9px; }
      .mb-chevron.open { transform: rotate(180deg); }

      .mb-toolbar-end { display: flex; gap: 8px; margin-left: auto; }
      .mb-action-btn {
        display: flex; align-items: center; gap: 7px;
        font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1.5px;
        text-transform: uppercase; padding: 9px 14px; cursor: pointer;
        border-radius: 2px; transition: all 0.2s;
      }
      .mb-btn-clear { color: rgba(139,26,26,0.65); border: 1px solid rgba(139,26,26,0.2); background: none; }
      .mb-btn-clear:hover { background: rgba(139,26,26,0.05); border-color: rgba(139,26,26,0.45); }
      .mb-btn-refresh { color: var(--gold-dim); border: 1px solid rgba(201,151,58,0.25); background: none; }
      .mb-btn-refresh:hover { background: rgba(201,151,58,0.07); border-color: var(--gold-mid); }
      .spin { animation: mbSpin 0.8s linear infinite; }

      /* ══════════════════════════════════════════════
         FILTER PANEL — refined, warm background
      ══════════════════════════════════════════════ */
      .mb-filter-panel {
        background: linear-gradient(180deg, #fdf6e8 0%, #fdf3e3 100%);
        border-bottom: 1px solid rgba(201,151,58,0.15);
      }
      .mb-fp-strip {
        height: 2px;
        background: linear-gradient(90deg, transparent, var(--gold-dim) 15%, var(--gold-bright) 45%, var(--gold-dim) 85%, transparent);
      }
      .mb-filter-grid {
        display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 16px; padding: 20px 32px 24px;
      }
      .mb-fi { display: flex; flex-direction: column; gap: 6px; }
      .mb-fi-range { grid-column: span 2; }
      .mb-fi-disabled { opacity: 0.38; pointer-events: none; }
      .mb-fi-label {
        display: flex; align-items: center; gap: 7px;
        font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700;
        letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim);
      }
      .mb-fi-note { font-size: 8px; color: rgba(74,26,8,0.38); font-style: italic; letter-spacing: 0; text-transform: none; font-family: 'EB Garamond', serif; }
      .mb-fi-select, .mb-fi-input {
        background: #fff; border: 1px solid rgba(201,151,58,0.22); border-radius: 2px;
        outline: none; font-family: 'EB Garamond', serif; font-size: 14px;
        color: var(--text-dark); padding: 8px 12px;
        transition: border-color 0.2s, box-shadow 0.2s;
        -webkit-appearance: none; appearance: none; cursor: pointer; width: 100%;
      }
      .mb-fi-select:focus, .mb-fi-input:focus {
        border-color: var(--gold-mid);
        box-shadow: 0 0 0 3px rgba(201,151,58,0.08);
      }
      .mb-range-row { display: flex; align-items: center; gap: 8px; }
      .mb-range-row .mb-fi-input { flex: 1; }
      .mb-range-sep { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(201,151,58,0.5); flex-shrink: 0; }
      .mb-fi-clear {
        font-family: 'Cinzel', serif; font-size: 7px; font-weight: 700;
        letter-spacing: 1.5px; text-transform: uppercase; color: rgba(139,26,26,0.55);
        background: none; border: none; cursor: pointer; padding: 0; text-align: left;
        transition: color 0.2s;
      }
      .mb-fi-clear:hover { color: #8b1a1a; }

      /* ══════════════════════════════════════════════
         RESULT INFO BAR
      ══════════════════════════════════════════════ */
      .mb-result-info {
        padding: 9px 32px;
        background: #fdf8ef;
        border-bottom: 1px solid rgba(201,151,58,0.10);
        font-family: 'EB Garamond', serif; font-size: 14px; color: rgba(74,26,8,0.5);
        display: flex; align-items: center; gap: 12px;
      }
      .mb-result-info strong { color: var(--gold-dim); font-weight: 600; }
      .mb-clear-inline {
        display: inline-flex; align-items: center; gap: 5px;
        font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700; letter-spacing: 1.5px;
        text-transform: uppercase; color: rgba(139,26,26,0.55);
        background: none; border: 1px solid rgba(139,26,26,0.18);
        padding: 3px 9px; cursor: pointer; border-radius: 2px; transition: all 0.2s;
      }
      .mb-clear-inline:hover { background: rgba(139,26,26,0.05); border-color: rgba(139,26,26,0.4); }

      /* ══════════════════════════════════════════════
         CARD GRID — responsive natural flow
      ══════════════════════════════════════════════ */
      .mb-content {
        padding: 32px 40px 60px;
        background: #fdf8ef;
        min-height: 200px;
      }
      .mb-card-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 28px;
        max-width: 1280px;
        margin: 0 auto;
        padding: 8px 4px;
      }

      /* ══════════════════════════════════════════════
         CARD — Professional Temple Ledger Style
      ══════════════════════════════════════════════ */
      .mb-card {
        display: flex; flex-direction: row;
        background: #fdf8f0;
        border: 1px solid rgba(201,151,58,0.22);
        position: relative; cursor: pointer; overflow: hidden;
        animation: mbCardIn 0.5s cubic-bezier(0.22,1,0.36,1) both;
        transition: transform 0.28s cubic-bezier(0.22,1,0.36,1),
                    box-shadow 0.28s ease,
                    border-color 0.22s ease;
        border-radius: 3px;
        box-shadow: 0 1px 4px rgba(26,4,4,0.06), 0 4px 16px rgba(26,4,4,0.05);
      }
      @keyframes mbCardIn {
        from { opacity: 0; transform: translateY(22px) scale(0.95); }
        to   { opacity: 1; transform: none; }
      }
      .mb-card:hover {
        transform: translateY(-7px);
        box-shadow:
          0 0 0 1px rgba(201,151,58,0.35),
          0 20px 48px rgba(26,4,4,0.14),
          0 8px 20px rgba(201,151,58,0.12);
        border-color: rgba(201,151,58,0.45);
      }
      .mb-card:hover .mb-card-hover-glow { opacity: 1; }

      /* Left accent bar */
      .mb-card-side-bar {
        width: 4px; flex-shrink: 0; border-radius: 3px 0 0 3px;
        transition: width 0.25s ease;
      }
      .mb-card:hover .mb-card-side-bar { width: 5px; }

      /* Inner wrapper */
      .mb-card-inner {
        flex: 1; min-width: 0;
        display: flex; flex-direction: column;
      }

      /* ── HEADER ZONE ── */
      .mb-card-header {
        background: linear-gradient(160deg, #1e0505 0%, #150303 55%, #0c0101 100%);
        padding: 14px 18px 16px;
        position: relative; overflow: hidden; flex-shrink: 0;
      }
      .mb-card-header-noise {
        position: absolute; inset: 0; pointer-events: none; opacity: 0.5;
        background:
          radial-gradient(ellipse 120% 80% at 80% 120%, rgba(201,151,58,0.07) 0%, transparent 55%),
          radial-gradient(ellipse 60% 40% at 10% 0%, rgba(180,80,80,0.06) 0%, transparent 60%),
          repeating-linear-gradient(-55deg, transparent 0, transparent 22px, rgba(201,151,58,0.018) 22px, rgba(201,151,58,0.018) 23px);
      }

      /* Top meta: date chip + status badge */
      .mb-card-header-top {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: 12px; position: relative; z-index: 1;
      }
      .mb-card-date-chip {
        display: flex; align-items: baseline; gap: 5px;
      }
      .mb-cdc-num {
        font-family: 'Cinzel', serif; font-size: 10.5px; font-weight: 700;
        letter-spacing: 1px;
        color: rgba(232,196,106,0.65);
      }
      .mb-cdc-yr {
        font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 0.5px;
        color: rgba(232,196,106,0.28);
      }
      .mb-card-status-badge {
        display: inline-flex; align-items: center; gap: 5px;
        font-family: 'Cinzel', serif; font-size: 7px; font-weight: 700;
        letter-spacing: 1.5px; text-transform: uppercase;
        padding: 3px 9px; border: 1px solid; border-radius: 20px;
      }
      .mb-ct-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }

      /* Avatar + category row */
      .mb-card-header-main {
        display: flex; align-items: center; gap: 12px;
        position: relative; z-index: 1; margin-bottom: 10px;
      }
      .mb-card-avatar {
        width: 44px; height: 44px; position: relative; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
      }
      .mb-card-avatar-ring {
        position: absolute; inset: 0; border-radius: 50%;
        background: conic-gradient(from 0deg,
          transparent 0deg, var(--gold-bright) 60deg, transparent 120deg,
          var(--gold-mid) 180deg, transparent 240deg, var(--gold-bright) 300deg, transparent 360deg
        ) border-box;
        border: 1.5px solid transparent;
        -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: destination-out; mask-composite: exclude;
        animation: mbSpin 12s linear infinite;
      }
      .mb-card-avatar-letter {
        width: 34px; height: 34px; border-radius: 50%;
        background: linear-gradient(145deg, #5a3006 0%, var(--gold-mid) 42%, var(--gold-bright) 60%, #b8820e 100%);
        display: flex; align-items: center; justify-content: center;
        font-family: 'Cinzel Decorative', cursive; font-size: 14px; font-weight: 900;
        color: #0e0101; position: relative; z-index: 1;
        box-shadow: 0 0 14px rgba(232,196,106,0.35), inset 0 1px 0 rgba(255,255,255,0.12);
        letter-spacing: 0;
      }
      .mb-card-cat {
        font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700;
        color: #fdf3e3;
        line-height: 1.3; letter-spacing: 0.3px;
        display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
        overflow: hidden;
        text-shadow: 0 1px 6px rgba(0,0,0,0.5);
      }

      /* Receipt row */
      .mb-card-receipt-row {
        display: flex; align-items: center; gap: 6px;
        position: relative; z-index: 1;
      }
      .mb-receipt-icon {
        color: rgba(201,151,58,0.35); font-size: 8px; flex-shrink: 0;
      }
      .mb-card-receipt-val {
        font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 600;
        color: rgba(232,196,106,0.35); letter-spacing: 1px;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }

      /* ── DOTTED DIVIDER ── */
      .mb-card-divider {
        display: flex; align-items: center; justify-content: center; gap: 5px;
        padding: 0 18px;
        height: 28px;
        background: linear-gradient(90deg, #fdf8f0, #fdf3e3 50%, #fdf8f0);
        position: relative;
      }
      .mb-card-divider::before, .mb-card-divider::after {
        content: ''; position: absolute; left: 18px; right: 18px; height: 1px;
        background: linear-gradient(90deg, transparent, rgba(201,151,58,0.15) 30%, rgba(201,151,58,0.15) 70%, transparent);
      }
      .mb-card-divider::before { top: 0; }
      .mb-card-divider::after  { bottom: 0; }
      .mb-card-divider-dot {
        width: 4px; height: 4px; border-radius: 50%;
        background: rgba(201,151,58,0.3);
        flex-shrink: 0;
      }
      .mb-card-divider-dot.large {
        width: 6px; height: 6px;
        background: rgba(201,151,58,0.5);
      }

      /* ── BODY ZONE ── */
      .mb-card-body {
        padding: 16px 18px 12px;
        display: flex; flex-direction: column; gap: 14px;
        flex: 1;
        background: linear-gradient(180deg, #fdf8f0 0%, #fdf3e3 100%);
      }

      /* Amount */
      .mb-card-amount-zone {
        display: flex; align-items: center; justify-content: space-between;
        padding-bottom: 12px;
        border-bottom: 1px solid rgba(201,151,58,0.12);
      }
      .mb-card-amount-lbl {
        font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700;
        letter-spacing: 2.5px; text-transform: uppercase;
        color: rgba(138,96,32,0.45);
      }
      .mb-card-amount {
        font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900;
        background: linear-gradient(135deg, #0d4d0d 0%, #277a27 40%, #3ab83a 65%, #227a22 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        filter: drop-shadow(0 1px 3px rgba(30,100,30,0.2));
        line-height: 1;
        letter-spacing: -0.5px;
      }

      /* Data grid */
      .mb-card-data-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
      }
      .mb-card-data-item {
        display: flex; flex-direction: column; gap: 3px;
        padding: 8px 10px;
        background: rgba(255,255,255,0.55);
        border: 1px solid rgba(201,151,58,0.1);
        border-radius: 2px;
        transition: background 0.2s;
      }
      .mb-card:hover .mb-card-data-item { background: rgba(255,255,255,0.8); }
      .mb-cdi-icon {
        color: var(--gold-dim); font-size: 9px; margin-bottom: 3px;
      }
      .mb-cdi-label {
        display: block;
        font-family: 'Cinzel', serif; font-size: 6px; font-weight: 700;
        letter-spacing: 1.5px; text-transform: uppercase;
        color: rgba(138,96,32,0.4);
      }
      .mb-cdi-value {
        display: block;
        font-family: 'EB Garamond', serif; font-size: 13px; font-weight: 600;
        color: var(--text-mid);
        line-height: 1.2;
      }

      /* ── FOOTER ── */
      .mb-card-footer {
        padding: 0 18px 16px;
        background: linear-gradient(180deg, #fdf3e3 0%, #faebd7 100%);
      }
      .mb-card-view-btn {
        width: 100%; position: relative; overflow: hidden;
        display: flex; align-items: center; justify-content: center; gap: 0;
        font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700;
        letter-spacing: 2.5px; text-transform: uppercase;
        color: #1a0404;
        background: linear-gradient(135deg,
          #5a3006 0%, #a07010 15%, var(--gold-mid) 30%,
          var(--gold-bright) 48%, #fffae8 52%, var(--gold-bright) 56%,
          var(--gold-mid) 72%, #a07010 85%, #5a3006 100%
        );
        border: none; padding: 10px 18px; cursor: pointer;
        border-radius: 1px;
        box-shadow: 0 3px 12px rgba(201,151,58,0.28), inset 0 1px 0 rgba(255,255,255,0.2);
        transition: box-shadow 0.22s, filter 0.22s, transform 0.15s;
      }
      .mb-card-view-btn:hover { filter: brightness(1.08); box-shadow: 0 5px 20px rgba(201,151,58,0.42); }
      .mb-card-view-btn:active { transform: scale(0.98); }

      .mb-btn-shimmer {
        position: absolute; top: -50%; left: -80%; width: 45%; height: 200%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
        transform: skewX(-22deg); pointer-events: none;
        transition: left 0.55s ease;
      }
      .mb-card:hover .mb-btn-shimmer { left: 130%; }

      .mb-btn-icon { font-size: 9px; position: relative; z-index: 1; margin-right: 7px; }
      .mb-btn-label { position: relative; z-index: 1; flex: 1; text-align: center; }
      .mb-btn-arrow {
        position: relative; z-index: 1; margin-left: 7px;
        font-size: 12px; font-family: serif;
        opacity: 0; transform: translateX(-6px);
        transition: opacity 0.22s, transform 0.22s;
        font-style: normal; font-weight: 400;
      }
      .mb-card:hover .mb-btn-arrow { opacity: 1; transform: translateX(0); }

      /* Hover glow */
      .mb-card-hover-glow {
        position: absolute; inset: 0; pointer-events: none;
        background: radial-gradient(ellipse at 50% 100%, rgba(232,196,106,0.06) 0%, transparent 65%);
        opacity: 0; transition: opacity 0.3s;
      }

      /* Keep old tag/badge classes for modal */
      .mb-card-tags-row { display: none; }
      .mb-card-tag { display: none; }
      .mb-ct-status { display: inline-flex; align-items: center; gap: 4px; font-family: 'Cinzel', serif; font-size: 6.5px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; padding: 4px 9px; border: 1px solid; border-radius: 1px; }

      /* Modal usage badges */
      .mb-source-badge { display: inline-flex; align-items: center; gap: 5px; font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 4px 11px; border: 1px solid; border-radius: 1px; }
      .mb-source-badge.online  { color: #1a547a; border-color: rgba(26,84,122,0.3); background: rgba(26,84,122,0.07); }
      .mb-source-badge.offline { color: #2a6b2a; border-color: rgba(42,107,42,0.3); background: rgba(42,107,42,0.07); }
      .mb-pay-mode-badge { font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700; letter-spacing: 1.5px; padding: 4px 11px; border: 1px solid rgba(201,151,58,0.25); background: rgba(201,151,58,0.07); color: var(--gold-dim); border-radius: 1px; }

      /* ══════════════════════════════════════════════
         LOADER & EMPTY
      ══════════════════════════════════════════════ */
      .mb-loader {
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        gap: 24px; padding: 120px 0;
      }
      .mb-loader-ring-wrap {
        position: relative; width: 80px; height: 80px;
        display: flex; align-items: center; justify-content: center;
        background: rgba(201,151,58,0.05); border-radius: 50%;
        box-shadow: inset 0 0 20px rgba(201,151,58,0.15);
      }
      .mb-loader-ring {
        position: absolute; inset: -6px; border-radius: 50%;
        border: 1.5px dashed rgba(201,151,58,0.4);
        animation: mbSpin 6s linear infinite;
      }
      .mb-loader-ring-inner {
        position: absolute; inset: 4px; border-radius: 50%;
        border: 1px dotted rgba(201,151,58,0.6);
        animation: mbSpin 10s linear infinite reverse;
      }
      .mb-loader-om {
        position: relative; z-index: 1;
        font-family: 'Cinzel Decorative', cursive; font-size: 38px;
        background: linear-gradient(135deg, #fffaf0 0%, var(--gold-bright) 40%, var(--gold-mid) 70%, #a06a15 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        animation: mbPulse 1.5s ease-in-out infinite alternate;
        filter: drop-shadow(0 2px 8px rgba(201,151,58,0.4));
      }
      .mb-loader p {
        font-family: 'EB Garamond', serif; font-style: italic; font-size: 16px; 
        color: rgba(74,26,8,0.5); letter-spacing: 1px;
        animation: mbPulse 1.5s ease-in-out infinite alternate 0.5s;
      }

      .mb-empty {
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        gap: 14px; padding: 100px 0; text-align: center;
      }
      .mb-empty-glyph { font-size: 44px; opacity: 0.45; }
      .mb-empty-title {
        font-family: 'Cinzel Decorative', cursive; font-size: 19px; font-weight: 900;
        background: linear-gradient(135deg, #3d0c0c, var(--crimson-deep));
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      }
      .mb-empty-sub {
        font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px;
        color: rgba(74,26,8,0.42); max-width: 300px; line-height: 1.6;
      }
      .mb-empty-clear {
        display: inline-flex; align-items: center; gap: 8px; margin-top: 6px;
        font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px;
        text-transform: uppercase; color: rgba(139,26,26,0.6);
        background: none; border: 1px solid rgba(139,26,26,0.22);
        padding: 9px 20px; cursor: pointer; border-radius: 2px; transition: all 0.2s;
      }
      .mb-empty-clear:hover { background: rgba(139,26,26,0.05); border-color: rgba(139,26,26,0.5); }

      /* ══════════════════════════════════════════════
         MODAL
      ══════════════════════════════════════════════ */
      .mb-overlay {
        position: fixed; inset: 0; z-index: 3000;
        background: rgba(10,2,2,0.78); backdrop-filter: blur(7px);
        display: flex; align-items: center; justify-content: center; padding: 24px;
        animation: mbOverlayIn 0.2s ease both;
      }
      @keyframes mbOverlayIn { from { opacity: 0; } to { opacity: 1; } }

      .mb-modal {
        width: 100%; max-width: 600px; max-height: 88vh;
        background: linear-gradient(160deg, #fdf8f0 0%, #fdf3e3 55%, #f9ead5 100%);
        border: 1px solid rgba(201,151,58,0.38);
        box-shadow:
          0 0 0 1px rgba(201,151,58,0.10),
          0 0 0 5px rgba(201,151,58,0.04),
          0 40px 100px rgba(10,2,2,0.50);
        overflow: hidden; display: flex; flex-direction: column; position: relative;
        animation: mbModalIn 0.32s cubic-bezier(0.23,1,0.32,1) both;
      }
      @keyframes mbModalIn { from { opacity: 0; transform: scale(0.94) translateY(16px); } to { opacity: 1; transform: none; } }

      .mb-modal-strip { height: 3px; background: linear-gradient(90deg,transparent,var(--gold-dim) 8%,var(--gold-bright) 28%,#fff8e7 50%,var(--gold-bright) 72%,var(--gold-dim) 92%,transparent); }
      .mb-mc { position: absolute; width: 9px; height: 9px; border-color: rgba(201,151,58,0.45); border-style: solid; }
      .mb-mc.tl { top: 11px; left: 11px; border-width: 1px 0 0 1px; }
      .mb-mc.tr { top: 11px; right: 11px; border-width: 1px 1px 0 0; }
      .mb-mc.bl { bottom: 11px; left: 11px; border-width: 0 0 1px 1px; }
      .mb-mc.br { bottom: 11px; right: 11px; border-width: 0 1px 1px 0; }

      .mb-modal-head { position: relative; flex-shrink: 0; }
      .mb-mh-bg {
        position: absolute; inset: 0;
        background:
          radial-gradient(ellipse at 50% 40%, rgba(120,25,25,0.5) 0%, transparent 60%),
          linear-gradient(170deg, #2c0a0a 0%, #1a0404 55%, #0e0101 100%);
      }
      .mb-mh-inner {
        position: relative; z-index: 1;
        display: flex; flex-direction: column; align-items: center;
        padding: 30px 60px 24px;
      }
      .mb-mh-icon-wrap {
        width: 48px; height: 48px; border-radius: 50%; position: relative;
        background: rgba(201,151,58,0.09); border: 1px solid rgba(201,151,58,0.26);
        display: flex; align-items: center; justify-content: center; margin-bottom: 13px;
      }
      .mb-mh-icon-ring {
        position: absolute; inset: -5px; border-radius: 50%;
        border: 1px dashed rgba(201,151,58,0.22);
        animation: mbSpin 9s linear infinite;
      }
      .mb-mh-icon { color: var(--gold-mid); font-size: 19px; position: relative; z-index: 1; }
      .mb-mh-eyebrow {
        font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700;
        letter-spacing: 3.5px; text-transform: uppercase; color: rgba(232,196,106,0.38);
        margin-bottom: 7px;
      }
      .mb-mh-receipt {
        font-family: 'Cinzel Decorative', cursive; font-size: 17px; font-weight: 900;
        background: linear-gradient(135deg, #fff8e7, var(--gold-bright), var(--gold-mid));
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        margin-bottom: 5px;
      }
      .mb-mh-cat {
        font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px;
        color: rgba(245,228,176,0.45); margin-bottom: 12px;
      }
      .mb-mh-badges { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
      .mb-modal-close {
        position: absolute; top: 14px; right: 14px; z-index: 2;
        width: 32px; height: 32px; border-radius: 50%;
        border: 1px solid rgba(201,151,58,0.22);
        background: rgba(201,151,58,0.07); color: var(--gold-dim); font-size: 12px;
        display: flex; align-items: center; justify-content: center; cursor: pointer;
        transition: background 0.2s, transform 0.25s;
      }
      .mb-modal-close:hover { background: rgba(201,151,58,0.16); transform: rotate(90deg); }

      .mb-modal-rule {
        display: flex; align-items: center; justify-content: center;
        height: 1px; margin: 0 28px;
        background: linear-gradient(90deg,transparent,rgba(201,151,58,0.3),transparent);
        position: relative;
      }
      .mb-rule-gem {
        position: absolute; font-size: 9px; color: var(--gold-mid);
        background: #fdf3e3; padding: 0 5px;
      }

      .mb-modal-body { flex: 1; overflow-y: auto; padding: 22px 28px 8px; }
      .mb-section-label {
        font-family: 'Cinzel', serif; font-size: 7.5px; font-weight: 700;
        letter-spacing: 3px; text-transform: uppercase; color: var(--gold-dim);
        margin-bottom: 12px; display: flex; align-items: center; gap: 10px;
      }
      .mb-section-label::after { content: ''; flex: 1; height: 1px; background: linear-gradient(90deg,rgba(201,151,58,0.28),transparent); }

      .mb-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .mb-di {
        display: flex; align-items: flex-start; gap: 12px; padding: 13px 14px;
        background: rgba(255,251,243,0.75); border: 1px solid rgba(201,151,58,0.13);
        border-radius: 1px; transition: background 0.18s;
      }
      .mb-di:hover { background: rgba(255,253,246,0.97); }
      .mb-di-icon {
        width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
        background: rgba(201,151,58,0.08); border: 1px solid rgba(201,151,58,0.18);
        display: flex; align-items: center; justify-content: center;
        color: var(--gold-mid); font-size: 11px; margin-top: 1px;
      }
      .mb-di-label {
        font-family: 'Cinzel', serif; font-size: 7px; font-weight: 700;
        letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 4px;
      }
      .mb-di-val { font-family: 'EB Garamond', serif; font-size: 14px; color: var(--text-dark); line-height: 1.4; }

      .mb-persons-list { display: flex; flex-direction: column; gap: 6px; }
      .mb-person-chip {
        display: flex; align-items: center; gap: 10px;
        padding: 10px 14px; background: rgba(255,251,243,0.85);
        border: 1px solid rgba(201,151,58,0.13); border-radius: 1px;
      }
      .mb-person-num {
        width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
        background: rgba(201,151,58,0.10); border: 1px solid rgba(201,151,58,0.22);
        display: flex; align-items: center; justify-content: center;
        font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; color: var(--gold-dim);
      }
      .mb-person-name { font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); flex: 1; }
      .mb-person-naksh {
        font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 600; letter-spacing: 1px;
        color: var(--gold-dim); padding: 3px 9px; border: 1px solid rgba(201,151,58,0.2);
        background: rgba(201,151,58,0.05); border-radius: 1px;
      }

      .mb-modal-foot {
        padding: 14px 28px 20px;
        border-top: 1px solid rgba(201,151,58,0.10);
        display: flex; justify-content: center;
      }
      .mb-close-btn {
        position: relative; overflow: hidden;
        display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2.5px;
        text-transform: uppercase; color: var(--crimson-deep); border: none; cursor: pointer;
        padding: 12px 28px;
        background: linear-gradient(135deg, #6b4209 0%, var(--gold-mid) 25%, var(--gold-bright) 45%, #fdf0c8 55%, var(--gold-bright) 65%, var(--gold-mid) 80%, #6b4209 100%);
        clip-path: polygon(12px 0%,calc(100% - 12px) 0%,100% 50%,calc(100% - 12px) 100%,12px 100%,0% 50%);
        box-shadow: 0 5px 20px rgba(201,151,58,0.28);
        transition: transform 0.22s, box-shadow 0.22s, filter 0.22s;
      }
      .mb-close-btn:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(201,151,58,0.45); filter: brightness(1.05); }
      .mb-close-shimmer {
        position: absolute; top: -50%; left: -70%; width: 40%; height: 200%;
        background: rgba(255,255,255,0.2); transform: skewX(-20deg);
        transition: left 0.5s ease; pointer-events: none;
      }
      .mb-close-btn:hover .mb-close-shimmer { left: 130%; }

      /* ══════════════════════════════════════════════
         TOAST
      ══════════════════════════════════════════════ */
      .mb-toast-wrap { position: fixed; top: 28px; right: 28px; z-index: 4000; animation: mbToastIn 0.38s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes mbToastIn { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: none; } }
      .mb-toast {
        width: 340px;
        background: linear-gradient(145deg, #FDF3E3, #faf0dc, #f5e8cc);
        border: 1px solid var(--border);
        border-radius: 2px;
        box-shadow: 0 12px 32px rgba(26,4,4,0.14);
        padding: 0 0 14px; overflow: hidden; position: relative;
      }
      .mb-toast-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .mb-tc { position: absolute; width: 9px; height: 9px; border-color: rgba(201,151,58,0.4); border-style: solid; }
      .mb-tc.tl { top: 11px; left: 11px; border-width: 1px 0 0 1px; }
      .mb-tc.tr { top: 11px; right: 11px; border-width: 1px 1px 0 0; }
      .mb-tc.bl { bottom: 7px; left: 11px; border-width: 0 0 1px 1px; }
      .mb-tc.br { bottom: 7px; right: 11px; border-width: 0 1px 1px 0; }
      .mb-toast-medal {
        position: absolute; left: 18px; top: 26px; width: 34px; height: 34px;
        border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center;
      }
      .mb-toast-spin { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.28); animation: mbSpin 8s linear infinite; }
      .mb-toast-body { padding: 0 40px 0 64px; }
      .mb-toast-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .mb-toast-title { font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .mb-toast-rule { height: 1px; margin-bottom: 6px; }
      .mb-toast-msg { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.72); line-height: 1.5; }
      .mb-toast-foot { height: 1px; margin: 12px 18px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.22),transparent); }
      .mb-toast-x { position: absolute; top: 20px; right: 13px; background: none; border: none; font-size: 12px; cursor: pointer; opacity: 0.4; transition: opacity 0.2s; }
      .mb-toast-x:hover { opacity: 0.85; }


      /* ══════════════════════════════════════════════
         TAB SELECTOR
      ══════════════════════════════════════════════ */
      .mb-tab-selector {
        display: flex; gap: 12px; margin-top: 24px; margin-bottom: 4px;
      }
      .mb-tab-btn {
        position: relative; overflow: hidden;
        display: flex; align-items: center; gap: 9px;
        font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700;
        letter-spacing: 2px; text-transform: uppercase;
        padding: 13px 28px;
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(201,151,58,0.22);
        color: rgba(232,196,106,0.45);
        cursor: pointer; border-radius: 2px;
        transition: all 0.28s cubic-bezier(0.22,1,0.36,1);
        flex: 1; justify-content: center;
      }
      .mb-tab-btn:hover {
        background: rgba(201,151,58,0.08);
        border-color: rgba(201,151,58,0.4);
        color: rgba(232,196,106,0.75);
      }
      .mb-tab-btn.active {
        background: linear-gradient(135deg,
          rgba(201,151,58,0.18) 0%, rgba(232,196,106,0.12) 50%, rgba(201,151,58,0.18) 100%);
        border-color: rgba(201,151,58,0.6);
        color: #e8c46a;
        box-shadow: 0 4px 20px rgba(201,151,58,0.2), inset 0 1px 0 rgba(232,196,106,0.12);
      }
      .mb-tab-btn.active::after {
        content: '';
        position: absolute; bottom: 0; left: 15%; right: 15%; height: 2px;
        background: linear-gradient(90deg, transparent, var(--gold-bright), transparent);
        border-radius: 2px 2px 0 0;
      }
      .mb-tab-icon { font-size: 11px; flex-shrink: 0; }
      .mb-tab-label { position: relative; z-index: 1; }
      .mb-tab-count {
        font-family: 'Cinzel', serif; font-size: 8px; font-weight: 900;
        background: rgba(201,151,58,0.25); border: 1px solid rgba(201,151,58,0.3);
        color: var(--gold-bright); border-radius: 10px;
        padding: 1px 7px; letter-spacing: 1px; flex-shrink: 0;
      }
      .mb-tab-btn.active .mb-tab-count {
        background: rgba(201,151,58,0.35);
        border-color: rgba(201,151,58,0.55);
      }

      /* Donation toolbar */
      .mb-don-toolbar {
        background: #fdf8ef;
        border-bottom: 1px solid rgba(201,151,58,0.18);
        padding: 10px 32px;
        display: flex; align-items: center; justify-content: space-between;
      }
      .mb-don-info {
        font-family: 'EB Garamond', serif; font-size: 14px;
        color: rgba(74,26,8,0.5);
      }
      .mb-don-info strong { color: var(--gold-dim); }

      /* ══════════════════════════════════════════════
         STAT ANIMATE
      ══════════════════════════════════════════════ */
      @keyframes mbStatPop {
        0%   { opacity: 0; transform: translateY(6px) scale(0.85); }
        60%  { transform: translateY(-2px) scale(1.08); }
        100% { opacity: 1; transform: none; }
      }
      .mb-stat-animate {
        animation: mbStatPop 0.38s cubic-bezier(0.22,1,0.36,1) both;
      }

      /* ══════════════════════════════════════════════
         RESPONSIVE
      ══════════════════════════════════════════════ */
      @media (max-width: 860px) {
        .mb-hero { grid-template-columns: 48px 1fr 48px; }
        .mb-hero-title { font-size: 24px; }
        .mb-stat-row { flex-wrap: nowrap; border: none; gap: 0; }
        .mb-stat { border: none; border-right: 1px solid rgba(201,151,58,0.15); padding: 10px 12px; flex: 1; min-width: 0; }
        .mb-stat:last-child { border-right: none; }
        .mb-stat-divider { display: none; }
        .mb-tab-btn { padding: 11px 16px; font-size: 9px; }
        .mb-card-grid { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
        .mb-card-data-grid { grid-template-columns: repeat(2, 1fr); }
        .mb-content { padding: 20px 20px 50px; }
        .mb-don-toolbar { padding: 10px 20px; }
      }
      @media (max-width: 640px) {
        /* Hero layout */
        .mb-hero { grid-template-columns: 32px 1fr 32px; min-height: unset; }
        .mb-hero-left-col, .mb-hero-right-col { padding: 16px 0; gap: 5px; }
        .mb-hlc-om { font-size: 13px; }
        .mb-hlc-symbol { font-size: 11px; }
        .mb-hlc-tick { width: 1.5px; }
        .mb-hero-center { padding: 22px 12px 20px; }
        .mb-hero-title { font-size: 19px; letter-spacing: -0.5px; }
        .mb-hero-sub { font-size: 12px; margin-bottom: 16px; }
        /* Tabs — full width, stacked feel */
        .mb-tab-selector { gap: 8px; margin-top: 16px; }
        .mb-tab-btn { padding: 10px 10px; font-size: 8.5px; letter-spacing: 1.5px; gap: 6px; }
        .mb-tab-icon { font-size: 10px; }
        .mb-tab-count { font-size: 7px; padding: 1px 5px; }
        /* Stats row */
        .mb-stat-row { width: 100%; gap: 0; border: 1px solid rgba(201,151,58,0.14); margin-top: 12px; }
        .mb-stat { padding: 8px 6px; flex: 1; min-width: 0; border-right: 1px solid rgba(201,151,58,0.12); }
        .mb-stat:last-child { border-right: none; }
        .mb-stat-val { font-size: 0.95rem !important; }
        .mb-stat-lbl { font-size: 5.5px; letter-spacing: 1px; }
        /* Toolbar */
        .mb-toolbar { padding: 10px 14px; flex-direction: column; align-items: stretch; gap: 8px; }
        .mb-toolbar-end { margin-left: 0; justify-content: stretch; }
        .mb-toolbar-end .mb-action-btn { flex: 1; justify-content: center; }
        .mb-action-btn { border-radius: 2px; justify-content: center; }
        .mb-search-wrap { width: 100%; }
        .mb-filter-btn { width: 100%; justify-content: center; }
        /* Content */
        .mb-content { padding: 14px 12px 50px; }
        .mb-don-toolbar { padding: 8px 14px; flex-direction: column; align-items: flex-start; gap: 6px; }
        /* Filter panel */
        .mb-filter-grid { grid-template-columns: 1fr; padding: 14px 14px 18px; gap: 10px; }
        .mb-fi-range { grid-column: span 1; }
        .mb-range-row { flex-direction: column; }
        .mb-range-sep { display: none; }
        /* Cards — single column on phone */
        .mb-card-grid { grid-template-columns: 1fr; gap: 12px; padding: 4px 0; }
        .mb-card-data-grid { grid-template-columns: repeat(3, 1fr); gap: 6px; }
        .mb-card-amount { font-size: 18px; }
        .mb-card-header { padding: 12px 14px 14px; }
        .mb-card-body { padding: 12px 14px 8px; }
        .mb-card-footer { padding: 0 14px 14px; }
        /* Modal */
        .mb-detail-grid { grid-template-columns: 1fr; }
        .mb-modal { max-height: 96vh; border-radius: 2px; }
        .mb-mh-inner { padding: 20px 36px 18px; }
        .mb-modal-body { padding: 14px; }
        /* Toast */
        .mb-toast-wrap { top: auto; bottom: 12px; right: 12px; left: 12px; }
        .mb-toast { width: 100%; border-radius: 8px; }
      }
      @media (max-width: 400px) {
        .mb-hero { grid-template-columns: 24px 1fr 24px; }
        .mb-hero-left-col, .mb-hero-right-col { padding: 12px 0; gap: 4px; }
        .mb-hlc-om { font-size: 10px; }
        .mb-hlc-symbol { font-size: 9px; }
        .mb-hero-title { font-size: 16px; }
        .mb-hero-sub { display: none; }
        .mb-tab-btn { padding: 9px 6px; font-size: 7.5px; letter-spacing: 1px; }
        .mb-tab-label { display: inline; }
        .mb-stat-val { font-size: 0.85rem !important; }
        .mb-stat-lbl { font-size: 5px; }
        .mb-card-amount { font-size: 15px; }
        .mb-card-cat { font-size: 12px; }
        .mb-cdi-value { font-size: 11px; }
      }
    `}</style>
  );
}